import { onDocumentReady } from "@story/story_assets/utils";

//
// One item in filter (service or article)
//
function FilterItem(element, type, area, serviceId = null) {
	this.element = element;
	this.type = type; // 'area', 'service', 'article'
	this.area = area;
	this.serviceId = serviceId;
}

FilterItem.prototype = {
	constructor: FilterItem,

	show: function() {
		this.element.style.display = '';
	},

	hide: function() {
		this.element.style.display = 'none';
	},

	activate: function() {
		this.element.classList.add('active');
	},

	deactivate: function() {
		this.element.classList.remove('active');
	},

	isVisible: function() {
		return this.element.style.display !== 'none';
	},

	isActive: function() {
		return this.element.classList.contains('active');
	},

	matchesArea: function(area) {
		return !area || this.area === area;
	},

	matchesService: function(serviceId) {
		return !serviceId || this.serviceId === serviceId;
	}
};

//
// Main class for filtering services
//
export function ServiceFilter(wrapper) {
	this.wrapper = wrapper;
	this.currentArea = null;
	this.currentServiceId = null;

	// Containers
	this.areasContainer = wrapper.querySelector('[data-filter="areas"]');
	this.servicesContainer = wrapper.querySelector('[data-filter="services"]');
	this.articlesContainer = wrapper.querySelector('[data-filter="articles"]');

	// Initialize items
	this.initializeItems();
	this.bindEvents();
	this.initializeState();
}

ServiceFilter.prototype = {
	constructor: ServiceFilter,

	//
	// Initialize all filter items
	//
	initializeItems: function() {
		this.areas = [];
		this.services = [];
		this.articles = [];

		// Areas
		const areaElements = this.wrapper.querySelectorAll('[data-area-filter]');
		for (let element of areaElements) {
			const area = element.getAttribute('data-area-filter');
			this.areas.push(new FilterItem(element, 'area', area));
		}

		// Services in navigation
		const serviceElements = this.wrapper.querySelectorAll('[data-service-area]');
		for (let element of serviceElements) {
			const area = element.getAttribute('data-service-area');
			const serviceId = element.getAttribute('data-service-id');
			this.services.push(new FilterItem(element, 'service', area, serviceId));
		}

		// Articles
		const articleElements = this.wrapper.querySelectorAll('[data-article-service-area]');
		for (let element of articleElements) {
			const area = element.getAttribute('data-article-service-area');
			const serviceId = element.getAttribute('data-article-service-id');
			this.articles.push(new FilterItem(element, 'article', area, serviceId));
		}

	},

	//
	// Bind event handlers
	//
	bindEvents: function() {
		// Click on areas
		for (let areaItem of this.areas) {
			areaItem.element.addEventListener('click', this.onAreaClick.bind(this));
		}

		// Click on services
		for (let serviceItem of this.services) {
			serviceItem.element.addEventListener('click', this.onServiceClick.bind(this));
		}
	},

	//
	// Handler click on area
	//
	onAreaClick: function(event) {
		event.preventDefault();
		const area = event.currentTarget.getAttribute('data-area-filter');
		this.filterByArea(area);
	},

	//
	// Handler click on service
	//
	onServiceClick: function(event) {
		event.preventDefault();
		const serviceId = event.currentTarget.getAttribute('data-service-id');
		this.filterByService(serviceId);
	},

	//
	// Filter by area
	//
	filterByArea: function(area) {
		this.currentArea = area;
		this.currentServiceId = null; // Reset service selection

		// Filter only navigation items and articles (cards zůstávají beze změny)
		this.filterItems(this.services, area, null);
		this.filterItems(this.articles, area, null);

		// Update active states
		this.updateActiveStates();

		// Auto-select first visible service
		this.autoSelectFirstService();

		// Trigger custom event
		this.triggerEvent('areaChanged', { area: area });
	},

	//
	// Filter by service
	//
	filterByService: function(serviceId) {
		this.currentServiceId = serviceId;

		// Filter only articles by service (keep area)
		this.filterItems(this.articles, this.currentArea, serviceId);

		// Update active states
		this.updateActiveStates();

		// Auto-select first visible article
		this.autoSelectFirstArticle();

		// Trigger custom event
		this.triggerEvent('serviceChanged', { serviceId: serviceId });
	},

	//
	// General method for filtering items
	//
	filterItems: function(items, area, serviceId) {
		for (let item of items) {
			const matchesArea = item.matchesArea(area);
			const matchesService = item.matchesService(serviceId);

			if (matchesArea && matchesService) {
				item.show();
			} else {
				item.hide();
			}
		}
	},

	//
	// Update active states
	//
	updateActiveStates: function() {
		// Deactivate all
		this.deactivateAll();

		// Activate current selection
		this.activateByArea(this.currentArea);
		this.activateByService(this.currentServiceId);
	},

	//
	// Deactivate all items
	//
	deactivateAll: function() {
		const allItems = [...this.areas, ...this.services, ...this.articles];
		for (let item of allItems) {
			item.deactivate();
		}
	},

	//
	// Activate by area
	//
	activateByArea: function(area) {
		for (let areaItem of this.areas) {
			if (areaItem.area === area) {
				areaItem.activate();
				break;
			}
		}
	},

	//
	// Activate by service
	//
	activateByService: function(serviceId) {
		for (let serviceItem of this.services) {
			if (serviceItem.serviceId === serviceId) {
				serviceItem.activate();
				break;
			}
		}
	},

	//
	// Auto-select first visible service
	//
	autoSelectFirstService: function() {
		const firstVisible = this.services.find(item => item.isVisible());
		if (firstVisible) {
			this.filterByService(firstVisible.serviceId);
		}
	},

	//
	// Auto-select first visible article
	//
	autoSelectFirstArticle: function() {
		const firstVisible = this.articles.find(item => item.isVisible());
		if (firstVisible) {
			firstVisible.activate();
		}
	},

	//
	// Initialize default state
	//
	initializeState: function() {
		if (this.areas.length > 0) {
			const firstArea = this.areas[0].area;
			this.filterByArea(firstArea);
		}
	},

	//
	// Trigger custom event
	//
	triggerEvent: function(eventName, detail) {
		const event = new CustomEvent(eventName, {
			detail: detail,
			bubbles: true
		});
		this.wrapper.dispatchEvent(event);
	},

	//
	// Public API for external control
	//
	setArea: function(area) {
		this.filterByArea(area);
	},

	setService: function(serviceId) {
		this.filterByService(serviceId);
	},

	getCurrentArea: function() {
		return this.currentArea;
	},

	getCurrentService: function() {
		return this.currentServiceId;
	}
};

//
// Initialize when page is loaded
//
onDocumentReady(function() {
	const filterWrapper = document.querySelector('.section-body');
	if (filterWrapper) {
		window.serviceFilter = new ServiceFilter(filterWrapper);
	}
});
